import dayjs from "dayjs";
import {useEffect, useRef} from "react";
import {isMobile} from "react-device-detect";
import type {FieldErrors, UseFormGetValues, UseFormSetValue} from "react-hook-form";
import SignatureCanvas from "react-signature-canvas";
import {Button} from "reactstrap";
import {z} from "zod";

export const step3Schema = z.object({
  signatureTechnicien: z.string().min(1, "Signature de l'intervenant(e) requise"),
  signatureTechnicienDate: z.string(),
});

export type Step3Form = z.infer<typeof step3Schema>;

type Props = {
  errors: FieldErrors<Step3Form>;
  disabled: boolean;
  getValues: UseFormGetValues<Step3Form>;
  setValue: UseFormSetValue<Step3Form>;
};

export function Step3Signatures({errors, disabled, getValues, setValue}: Props) {
  const techSignatureRef = useRef<SignatureCanvas>(null);

  useEffect(() => {
    const savedTechSig = getValues("signatureTechnicien");
    if (savedTechSig && techSignatureRef.current) {
      const img = new Image();
      img.onload = () => {
        if (!techSignatureRef.current) {
          return;
        }
        const {width, height} = techSignatureRef.current.getCanvas();
        techSignatureRef.current.fromDataURL(savedTechSig, {width, height});
      };
      img.src = savedTechSig;
    }
  }, [getValues]);

  const canvasWidth = isMobile ? 280 : 450;
  const canvasHeight = isMobile ? 120 : 200;

  return (
    <>
      <h5 style={{marginBottom: "12px"}}>Signature</h5>

      <p style={{marginBottom: "4px", fontSize: "14px"}}>
        <strong>Intervenant(e)</strong> (obligatoire)
      </p>
      {errors.signatureTechnicien && (
        <p style={{color: "red", fontSize: "12px", margin: 0}}>
          {errors.signatureTechnicien.message}
        </p>
      )}
      <div
        style={{
          border: "1px solid #ccc",
          borderRadius: "4px",
          backgroundColor: "#fff",
          marginBottom: "4px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <SignatureCanvas
          ref={techSignatureRef}
          canvasProps={{
            width: canvasWidth,
            height: canvasHeight,
            className: "signature-canvas",
            style: {width: `${canvasWidth}px`, height: `${canvasHeight}px`},
          }}
          backgroundColor="white"
          onEnd={handleTechSignatureEnd}
        />
      </div>
      <div style={{display: "flex", justifyContent: "flex-end", marginBottom: "12px"}}>
        <Button color="secondary" onClick={handleClearTech} disabled={disabled} size="sm">
          Recommencer
        </Button>
      </div>
    </>
  );

  function handleTechSignatureEnd() {
    if (techSignatureRef.current) {
      const isEmpty = techSignatureRef.current.isEmpty();
      const dataUrl = isEmpty ? "" : techSignatureRef.current.toDataURL();
      setValue("signatureTechnicien", dataUrl);
      if (!isEmpty) {
        setValue("signatureTechnicienDate", dayjs().toISOString());
      }
    }
  }

  function handleClearTech() {
    if (techSignatureRef.current) {
      techSignatureRef.current.clear();
      setValue("signatureTechnicien", "");
      setValue("signatureTechnicienDate", "");
    }
  }
}
